import clsx from 'clsx';
import React, { useMemo, useReducer, useState } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { Column } from 'react-table';
import { toTitleCase } from '../../../../../../_metronic/helpers';
import { api } from '../../../../../modules/api/api';
import { useAuth } from '../../../../../modules/auth';
import Avatar from '../../../../../modules/components/avatar/Avatar';
import Datatable from '../../../../../modules/components/datatable/Datatable';
import Header from '../../../../../modules/components/datatable/Header';
import UnlabeledBulletColumns from '../../../../../modules/components/list/UnlabeledBulletColumns';
import ListLoading from '../../../../../modules/components/loading/ListLoading';
import { useToast } from '../../../../../modules/components/toast/ToastContext';
import { isoCountryMap } from '../../../../../modules/helpers/isoCountryMap';
import { DataSiftField, SiService, siteIntelligenceServiceLabel } from '../../../mappings';
import EditableCardContainer from '../card/EditableCardContainer';
import { AddWebContent } from '../form/AddWebContent';
import { ServiceSelector, UsePageDataManagerResult } from '../hooks/usePageDataManager';
import { HistoryModal } from '../modal/HistoryModal';
import SummaryTable, { SummaryRow } from '../table/SummaryTable';
import { getFieldUpdate, getTranslatedValueWithFallback, SiRequestUser } from '../utils';

interface Sort {
  name: string;
  sort: 1 | -1;
}

interface Props {
  data: any;
  translation: { key: string, value: string, language: 'sc' }[];
  currentLanguage: 'sc' | 'en' | 'tc';
  refetch: () => void;
}

export default function ComplianceAdviceDetail({
  data,
  translation,
  currentLanguage,
  refetch,
}: Props) {
  const { currentUser, auth, refresh } = useAuth();
  const intl = useIntl();
  const { setToast } = useToast();
  const t = (id: string) => intl.formatMessage({ id: `SI_COMPLIANCE_ADVICE_DETAIL.${id}` });
  const emDash = '—';

  const results = data;
  const advicePoints = useMemo(() => {
    const advice = getTranslatedValueWithFallback(
      translation,
      currentLanguage,
      'advice',
      results?.result?.advice
    )
    if (!Array.isArray(advice)) {
      return [] as string[];
    }

    return advice
      .map((advice: unknown) => (typeof advice === 'string' ? advice.trim() : ''))
      .filter((advice: string) => advice.length > 0);
  }, [results, translation, currentLanguage]);

  return (
    <>
      <EditableCardContainer
        title={t('TITLE')}
      >
        <div className='p-9 vstack gap-9'>
          <div>
            {advicePoints.length > 0 ? (
              <div className='vstack gap-4'>
                {advicePoints.map((advice, index) => (
                  <div key={`${advice}-${index}`} className='d-flex align-items-center gap-4'>
                    <span className='badge badge-circle badge-light-primary fw-bolder'>
                      {index + 1}
                    </span>
                    <span className='text-gray-800 fs-6 lh-lg'>{advice}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className='text-gray-600 fs-6'>{emDash}</div>
            )}
          </div>
        </div>
        <div className='d-flex justify-content-end mb-5'>
          <a href='#summary' className='px-8 d-none print-display-button'>
            <i className="bi bi-caret-up-square-fill fs-3"></i>
          </a>
        </div>
      </EditableCardContainer>
    </>
  );
}
